// En enda källa för hreflang på hela sajten.
//
// Bakgrund: hreflang sattes tidigare per route-fil, vilket gav en delvis karta
// (324 svenska och 27 engelska sidor helt utan hreflang). En ofullständig karta
// är sämre än ingen alls — Google får då gissa språkversion i internationella
// SERP:ar. Nu genereras setet centralt i __root.tsx för varje sökväg, så en ny
// sida inte kan glömma bort det.
//
// Regler:
// - Sida med språkpar (ROUTE_PAIRS): sv + en + x-default (x-default = svenska).
// - Sida utan par: självrefererande hreflang för sitt eget språk + x-default.
//   Ett kluster med en medlem är giltigt och säger "det här är enda versionen".
// - Konsoliderad sida (canonical pekar bort): ingen hreflang. Canonical avgör
//   redan vilken URL som gäller; hreflang där skickar motstridiga signaler.

import { detectLocale, hasEnglishMirror, toEnPath, toSvPath } from "./routeMap";
import { isPausedPhotoPath } from "@/lib/paused-photo";
import { isConsolidated } from "@/lib/seo/canonical-overrides";

const SITE = "https://fenny.se";

// React vill ha camelCase (hrefLang) på DOM-noden; den serialiseras till
// attributet hreflang i HTML, vilket är det Google läser.
export type AlternateLink = { rel: "alternate"; hrefLang: string; href: string };

/** Normalisera bort trailing slash och query så nycklarna matchar routeMap. */
export function normalizePath(pathname: string): string {
  const clean = pathname.split("?")[0]!.split("#")[0]!;
  if (clean !== "/" && clean.endsWith("/")) return clean.slice(0, -1);
  return clean || "/";
}

export function hreflangLinks(pathname: string): AlternateLink[] {
  const path = normalizePath(pathname);

  // Systemvägar och filer ska aldrig ha hreflang.
  if (path.startsWith("/api") || path.includes(".") || path.startsWith("/admin") || path === "/login") {
    return [];
  }

  // Canonical pekar bort → hreflang hör hemma på primärsidan, inte här.
  if (isConsolidated(path) || isPausedPhotoPath(path)) return [];

  const locale = detectLocale(path);

  if (locale === "sv") {
    if (hasEnglishMirror(path)) {
      const en = toEnPath(path);
      return [
        { rel: "alternate", hrefLang: "sv", href: `${SITE}${path}` },
        { rel: "alternate", hrefLang: "en", href: `${SITE}${en}` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE}${path}` },
      ];
    }
    // Endast svensk version — självrefererande kluster.
    return [
      { rel: "alternate", hrefLang: "sv", href: `${SITE}${path}` },
      { rel: "alternate", hrefLang: "x-default", href: `${SITE}${path}` },
    ];
  }

  // Engelsk sida. toSvPath faller tillbaka till "/" när paret saknas — då är
  // det ingen riktig spegling och vi håller klustret till engelska.
  const sv = toSvPath(path);
  const hasPair = sv !== "/" || path === "/en";
  if (hasPair) {
    return [
      { rel: "alternate", hrefLang: "sv", href: `${SITE}${sv}` },
      { rel: "alternate", hrefLang: "en", href: `${SITE}${path}` },
      { rel: "alternate", hrefLang: "x-default", href: `${SITE}${sv}` },
    ];
  }
  return [
    { rel: "alternate", hrefLang: "en", href: `${SITE}${path}` },
    { rel: "alternate", hrefLang: "x-default", href: `${SITE}${path}` },
  ];
}
